import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** Prevent overlapping loads */
  #isRenderingNext = false;
  #isRenderingPrevious = false;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** Max live (non-gutted) pages in DOM at once */
  static MAX_LIVE_PAGES = 4;

  /** @type {Map<string, string>} - Stores innerHTML of gutted cards keyed by page-index */
  #guttedCardCache = new Map();

  connectedCallback() {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // Skip all scroll/observer overhead for single-page carousels (e.g. homepage product lists)
    const grid = this.querySelector('[ref="grid"]');
    const isSinglePage = grid?.dataset.lastPage === '1';

    if (!isSinglePage) {
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#observeViewMore();
      this.#initScrollWatcher();
    }

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '200px 0px 4000px 0px',
        }
      );
    }

    try { this.infinityScrollObserver?.disconnect(); } catch (e) {}
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  // --- Full innerHTML gutting with cached restore ---

  #gutPageCards(grid, pageNum) {
    grid.querySelectorAll(':scope > [ref="cards[]"][data-page="' + pageNum + '"]').forEach((card, i) => {
      if (card.dataset.imagesGutted) return;
      this.#guttedCardCache.set(pageNum + '-' + i, card.innerHTML);
      card.style.height = card.getBoundingClientRect().height + 'px';
      card.style.overflow = 'hidden';
      card.innerHTML = '';
      card.dataset.imagesGutted = 'true';
    });
  }

  #restorePageCards(grid, pageNum) {
    grid.querySelectorAll(':scope > [ref="cards[]"][data-page="' + pageNum + '"]').forEach((card, i) => {
      if (!card.dataset.imagesGutted) return;
      const cached = this.#guttedCardCache.get(pageNum + '-' + i);
      if (cached) {
        card.innerHTML = cached;
        card.style.height = '';
        card.style.overflow = '';
        delete card.dataset.imagesGutted;
        this.#guttedCardCache.delete(pageNum + '-' + i);
      }
    });
  }

  #gutOldPages(grid, direction) {
    const allCards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const livePages = new Set();
    allCards.forEach(c => { if (!c.dataset.imagesGutted) livePages.add(c.dataset.page); });

    if (livePages.size <= PaginatedList.MAX_LIVE_PAGES) return;

    const sortedPages = [...livePages].map(Number).sort((a, b) => a - b);

    if (direction === 'down') {
      const pagesToGut = sortedPages.slice(0, sortedPages.length - PaginatedList.MAX_LIVE_PAGES);
      pagesToGut.forEach(p => this.#gutPageCards(grid, p));
    } else {
      const pagesToGut = sortedPages.slice(PaginatedList.MAX_LIVE_PAGES);
      pagesToGut.forEach(p => this.#gutPageCards(grid, p));
    }
  }

  // --- Scroll watcher for restoring gutted cards on scroll-up ---

    #initScrollWatcher() {
    if (this._scrollWatcher) return;
    this._scrollWatcher = true;

    let ticking = false;
    const check = () => {
      const { grid } = this.refs;
      if (!grid) return;

      const vpTop = window.scrollY - window.innerHeight * 3;
      const vpBottom = window.scrollY + window.innerHeight * 4;

      // Find all gutted cards and restore any near the viewport
      const guttedPages = new Set();
      grid.querySelectorAll(':scope > [ref="cards[]"][data-images-gutted]').forEach(card => {
        const rect = card.getBoundingClientRect();
        const cardTop = rect.top + window.scrollY;
        if (cardTop > vpTop && cardTop < vpBottom) {
          guttedPages.add(Number(card.dataset.page));
        }
      });

      if (guttedPages.size === 0) return;

      guttedPages.forEach(pageNum => {
        this.#restorePageCards(grid, pageNum);
      });


      // After restoring, gut pages that are now too far away
      const allCards = grid.querySelectorAll(':scope > [ref="cards[]"]');
      const livePages = new Set();
      allCards.forEach(c => { if (!c.dataset.imagesGutted) livePages.add(Number(c.dataset.page)); });

      if (livePages.size > PaginatedList.MAX_LIVE_PAGES) {
        const sorted = [...livePages].sort((a, b) => a - b);
        // Find which pages are closest to current scroll position
        const currentCard = document.elementFromPoint(window.innerWidth / 2, window.innerHeight / 2);
        const currentPage = currentCard?.closest('[data-page]')?.dataset.page;
        const currentPageNum = Number(currentPage) || sorted[Math.floor(sorted.length / 2)];

        // Keep pages centered around current position
        const half = Math.floor(PaginatedList.MAX_LIVE_PAGES / 2);
        const keepStart = Math.max(sorted[0], currentPageNum - half);
        const keepEnd = keepStart + PaginatedList.MAX_LIVE_PAGES - 1;

        sorted.forEach(p => {
          if (p < keepStart || p > keepEnd) {
            this.#gutPageCards(grid, p);
          }
        });
      }
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          check();
          ticking = false;
        });
      }
    }, { passive: true });
  }

  async #renderNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    if (this.#isRenderingNext) return;
    this.#isRenderingNext = true;

    try {
      this.infinityScrollObserver?.disconnect();
    } catch (e) {}

    try {
      const nextPage = this.#getPage('next');
      if (!nextPage || !this.#shouldUsePage(nextPage)) return;

      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      grid.append(...nextPageItemElements);

      this.#gutOldPages(grid, 'down');

      this.#aspectRatioHelper?.processNewElements();

      if (window.scrollY > 200) {
        history.replaceState('', '', nextPage.url.toString());
      }

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } finally {
      this.#isRenderingNext = false;

      this.#observeViewMore();
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;
    if (!grid) return;

    if (this.#isRenderingPrevious) return;
    this.#isRenderingPrevious = true;

    try {
      try {
        this.infinityScrollObserver?.disconnect();
      } catch (e) {}

      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) return;

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      this.#gutOldPages(grid, 'up');

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      this.#isRenderingPrevious = false;

      this.#observeViewMore();
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid } = this.refs;
    const isPrevious = type === 'previous';

    if (!grid) return;

    let edgeEl;
    if (isPrevious) {
      edgeEl = grid.querySelector(':scope > [ref="cards[]"]:not([data-images-gutted])');
    } else {
      const allCards = grid.querySelectorAll(':scope > [ref="cards[]"]:not([data-images-gutted])');
      edgeEl = allCards.length ? allCards[allCards.length - 1] : null;
    }
    if (!(edgeEl instanceof HTMLElement)) return;

    const currentCardPage = Number(edgeEl.dataset.page);
    if (!Number.isFinite(currentCardPage)) return;

    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return { page, url };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#guttedCardCache.clear();

    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const currentLastPage = this.refs.grid?.dataset.lastPage;

    const observer = new MutationObserver(() => {
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        if (!this.isConnected) {
          return;
        }

        this.#observeViewMore();
        this.#fetchPage('next');
      }
    });

    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });

      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
